import React, { useState, useMemo } from "react";
import "./tablist.scss";
import CustomDatepicker from "../CustomDatepicker/CustomDatepicker";
import CustomButton from "../CustomButton/CustomButton";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import ImportantText from "../ImportantText/ImportantText";
import { useStations } from "../../hooks/useStations";

const SELECTION = {
  FLIGHT: "flight",
  ROUTE: "route",
};

const buildOptions = (stations) =>
  Object.entries(stations)
    .sort((a, b) => a[1].localeCompare(b[1]))
    .map(([code, city]) => ({
      value: code,
      searchTerm: `${city} ${code}`,
      label: (
        <div className="custom-option">
          <div className="cities">{city}</div>
          <div className="airport-code">{code}</div>
        </div>
      ),
    }));


const flightSearchSchema = yup
  .object({
    flightDate: yup.date().required("Please enter a date"),
    flightNumber: yup
      .string()
      .matches(/^[a-zA-Z0-9]+$/, "Please enter a valid flight number")
      .test(
        "max-length",
        `Maximum length is 6 characters`,
        (value) => value.length <= 6
      ),
  })
  .required();

const flightRouteSchema = yup
  .object({
    flightDate: yup.date().required("Please enter a date"),
    departureStation: yup.string(),
    arrivalStation: yup.string(),
  })
  .required();

const SearchByFlight = ({ selection }) => {
  const [searchParams] = useSearchParams();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(flightSearchSchema),
    defaultValues: {
      flightNumber: searchParams.get("flightNumber") || "",
    },
  });
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onSubmit = async (d) => {
    navigate(
      `/search-results?tab=${selection}&flightNumber=${d.flightNumber
      }&flightDate=${d.flightDate.toLocaleDateString("en-CA")}`
    );
  };

  return (
    <>
      <div>
        <div className="input-fields">
          <div
            className={`input-wrapper ${errors?.flightNumber?.message.length > 0 && "error"
              }`}
          >
            <div className="prefix">F8</div>
            <input
              className={`custom-input`}
              placeholder={`${t("status-page.flightHeaderPlaceholder")}`}
              {...register("flightNumber")}
            />
          </div>
          <p className="error-message">{errors.flightNumber?.message}</p>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomDatepicker
                placeholder={`${t("status-page.departureDatePlaceholder")}`}
                onChange={onChange}
                selected={value}
              />
            )}
            name="flightDate"
          />
        </div>
        <CustomButton
          onClick={handleSubmit(onSubmit)}
          type="submit"
          classnames="mb-24 primary"
          disabled={!isValid}
          name={`${t("status-page.search")}`}
        />
      </div>
      <div className="display-mobile">
        <ImportantText />
      </div>
    </>
  );
};

const SearchByRoute = ({ selection, stations, options, getDestinationsForOrigin, getOriginsForDestination }) => {
  const [searchParams] = useSearchParams();
  const [departureStation, setDepartureStation] = useState(
    searchParams.get("departure") || ""
  );
  const [arrivalStation, setArrivalStation] = useState(
    searchParams.get("arrival") || ""
  );

  const {
    register,
    handleSubmit,
    setValue,
    control,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(flightRouteSchema),
    defaultValues: {
      departureStation: searchParams.get("departure") || "",
      arrivalStation: searchParams.get("arrival") || "",
    },
  });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onSubmit = (d) => {
    navigate(
      `/search-results?tab=${selection}&departureStation=${d.departureStation
      }&arrivalStation=${d.arrivalStation
      }&flightDate=${d.flightDate.toLocaleDateString("en-CA")}`
    );
  };

  const handleDepartureChange = (e) => {
    setValue("departureStation", e.value);
    setDepartureStation(e.value);
  };

  const handleArrivalChange = (e) => {
    setValue("arrivalStation", e.value);
    setArrivalStation(e.value);
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "100%",
      border: "1px solid #D4D4D4",
      boxShadow: "none",
      "&:hover": {
        border: "2px solid #8C0AC8",
        padding: 0,
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      visibility: "hidden",
    }),
    menu: (provided) => ({
      ...provided,
      color: "#262626",
      fontWeight: 600,
    }),
    option: (base) => ({
      ...base,
      "&:hover": {
        backgroundColor: "#f4e7fa",
      },
      "&:focus": {
        backgroundColor: "#f4e7fa",
        border: "1px solid purple",
      },
    }),
    menuList: (base) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "4px",
        height: "0px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f1f1f1",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#D4D4D4",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#555",
      },
    }),
  };

  const customFilter = (option, searchText) => {
    if (
      option?.data.searchTerm.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      <div className="input-fields-route">
        <Controller
          control={control}
          name="departureStation"
          render={({ field }) => {
            const validOrigins = arrivalStation
              ? getOriginsForDestination(arrivalStation)
              : stations;
            const filteredOptions = buildOptions(validOrigins);

            return (
              <Select
                placeholder={<span>{t("status-page.from")}</span>}
                styles={selectStyles}
                {...field}
                options={filteredOptions}
                isSearchable={true}
                value={
                  departureStation
                    ? {
                      label: `${stations[departureStation]} - ${departureStation}`,
                      key: departureStation,
                    }
                    : null
                }
                onChange={(e) => {
                  handleDepartureChange(e);
                  return field.onChange;
                }}
                filterOption={customFilter}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="arrivalStation"
          render={({ field }) => {
            const validDestinations = departureStation
              ? getDestinationsForOrigin(departureStation)
              : stations;
            const filteredOptions = buildOptions(validDestinations);

            return (
              <Select
                placeholder={<span>{t("status-page.to")}</span>}
                styles={selectStyles}
                {...field}
                options={filteredOptions}
                isSearchable={true}
                value={
                  arrivalStation
                    ? {
                      label: `${stations[arrivalStation]} - ${arrivalStation}`,
                      key: arrivalStation,
                    }
                    : null
                }
                onChange={(e) => {
                  handleArrivalChange(e);
                  return field.onChange;
                }}
                filterOption={customFilter}
              />
            );
          }}
        />
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <CustomDatepicker
              placeholder={`${t("status-page.selectDepartureDate")}`}
              onChange={onChange}
              selected={value}
            />
          )}
          name="flightDate"
        />
      </div>
      <CustomButton
        onClick={handleSubmit(onSubmit)}
        type="submit"
        classnames="primary"
        disabled={!isValid}
        name={`${t("status-page.search")}`}
      />
      <div className="display-mobile">
        <ImportantText />
      </div>
    </>
  );
};

const TabList = () => {
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState(
    searchParams.get("tab") || SELECTION.FLIGHT
  );
  const { t } = useTranslation();
  const { stations, getDestinationsForOrigin, getOriginsForDestination } = useStations();
  const options = useMemo(() => buildOptions(stations), [stations]);

  return (
    <>
      <div className="radio-inputs">
        <button
          onClick={() => setSelection(SELECTION.FLIGHT)}
          className={selection === SELECTION.FLIGHT ? "checked" : ''}
        >
          <p className={selection === SELECTION.FLIGHT ? "selected-name" : "name"}>{`${t("status-page.byflightNumberHeader")}`}</p>
        </button>

        <button
          onClick={() => setSelection(SELECTION.ROUTE)}
          className={selection === SELECTION.ROUTE ? "checked" : ''}
        >
          <p className={selection === SELECTION.ROUTE ? "selected-name" : "name"}>{`${t("status-page.byRouteHeader")}`}</p>
        </button>
      </div>

      {selection === SELECTION.FLIGHT ? (
        <SearchByFlight selection={selection} />
      ) : (
        <SearchByRoute
          selection={selection}
          stations={stations}
          options={options}
          getDestinationsForOrigin={getDestinationsForOrigin}
          getOriginsForDestination={getOriginsForDestination}
        />
      )}
    </>
  );
};

export default TabList;
